// The Queuing tab as Qmatic draws it: services → segmentation lanes (each line ends in
// its queue) → work profiles. One grid row per group; the connectors are drawn from
// the boxes' measured places.
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Button, Chip } from '@heroui/react'
import { buildFlow, highlight, type NodeId } from '../../lib/profilesGraph'
import { LIMITS } from '../../lib/profilesValidate'
import { nameIn, type StaffKey } from '../../lib/staffText'
import type { OperationDoc, ProfileProblem, ProfileQueue, ProfileSegment, Service } from '../../lib/types'
import { useSize } from '../../reports/insightVisuals'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Connectors, type Anchor } from './Connectors'

export type Open = { kind: 'group' | 'segment' | 'queue' | 'work'; id: string; groupId?: string }
type AddKind = 'group' | 'queue' | 'work' | 'segment'

const SERVICE_LINES = 6

export function Schematic({ doc, services, pins, readOnly, onOpen, onAdd }: {
  doc: OperationDoc
  services: Service[]
  pins: Map<string, ProfileProblem[]>
  readOnly: boolean
  onOpen: (o: Open) => void
  onAdd: (kind: AddKind, groupId?: string) => void
}) {
  const { lang, say } = useStaffText()
  const flow = useMemo(() => buildFlow(doc), [doc])
  const [hover, setHover] = useState<NodeId | null>(null)
  const lit = useMemo(() => (hover ? highlight(flow, hover) : null), [flow, hover])

  // Anchors: every node's element, measured against the grid after each layout.
  const els = useRef(new Map<NodeId, HTMLElement>())
  const refs = useRef(new Map<NodeId, (el: HTMLElement | null) => void>())
  const anchor = useCallback((id: NodeId) => {
    let ref = refs.current.get(id)
    if (!ref) {
      ref = (el) => { if (el) els.current.set(id, el); else els.current.delete(id) }
      refs.current.set(id, ref)
    }
    return ref
  }, [])
  const [setSized, size] = useSize<HTMLDivElement>()
  const grid = useRef<HTMLDivElement | null>(null)
  const setGrid = useCallback((el: HTMLDivElement | null) => { grid.current = el; setSized(el) }, [setSized])
  const [moved, setMoved] = useState(0)
  useEffect(() => {
    const g = grid.current
    if (!g || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setMoved((n) => n + 1))
    for (const el of g.children) ro.observe(el)
    return () => ro.disconnect()
  }, [doc])
  const [layout, setLayout] = useState<{ anchors: Map<NodeId, Anchor>; w: number; h: number }>({ anchors: new Map(), w: 0, h: 0 })
  useLayoutEffect(() => {
    const g = grid.current
    if (!g) return
    const base = g.getBoundingClientRect()
    const anchors = new Map<NodeId, Anchor>()
    for (const [id, el] of els.current) {
      const r = el.getBoundingClientRect()
      anchors.set(id, { l: r.left - base.left, r: r.right - base.left, y: r.top - base.top + r.height / 2, h: r.height })
    }
    setLayout({ anchors, w: Math.ceil(base.width), h: Math.ceil(base.height) })
  }, [doc, size, moved, lang, pins])

  const hoverOn = (id: NodeId) => ({
    onPointerEnter: (e: React.PointerEvent) => { if (e.pointerType !== 'touch') setHover(id) },
    onPointerLeave: () => setHover(null),
    onFocus: () => setHover(id),
    onBlur: () => setHover(null),
  })
  const node = (id: NodeId, open: Open) => ({
    ref: anchor(id),
    'data-node': id,
    'data-lit': lit?.nodes.has(id) || undefined,
    'data-dim': (lit && !lit.nodes.has(id)) || undefined,
    ...hoverOn(id),
    onClick: () => onOpen(open),
  })

  const pinned = (problems: ProfileProblem[] | undefined) => {
    if (!problems?.length) return {}
    return problems.some((p) => !p.warning) ? { 'data-bad': true } : { 'data-warn': true }
  }
  const badge = (problems: ProfileProblem[] | undefined) =>
    problems?.length ? (
      <span className="pf-badge" title={say('profiles.nProblems', { n: problems.length })}>
        {problems.length}
      </span>
    ) : null
  const add = (kind: AddKind, label: StaffKey, groupId?: string, full = false) =>
    readOnly ? null : (
      <Button
        variant="ghost"
        size="sm"
        className="pf-add"
        isDisabled={full}
        aria-description={full ? say('profiles.problem.TOO_MANY', { max: LIMITS.segmentsPerGroup }) : undefined}
        onPress={() => onAdd(kind, groupId)}
      >
        <Icon name="plus" size={14} /> {say(label)}
      </Button>
    )

  const serviceName = new Map(services.map((s) => [s.id, nameIn(s.name, lang, s.code)]))
  const queues = new Map(doc.queues.map((q) => [q.id, q]))
  const groups = new Map(doc.groups.map((g) => [g.id, g]))
  const work = new Map(doc.workProfiles.map((w) => [w.id, w]))

  // What a rule asks for, in a few words: services, priority, language, source, booking.
  const conditions = (s: ProfileSegment): string =>
    [
      ...(s.serviceIds ?? []).map((id) => serviceName.get(id) ?? say('profiles.sheet.serviceGone')),
      ...(s.priorities ?? []).map((p) => say(`profiles.seg.p.${p}` as StaffKey)),
      ...(s.languages ?? []).map((l) => l.toUpperCase()),
      ...(s.sources ?? []).map((x) => say(`profiles.seg.src.${x}` as StaffKey)),
      ...(s.appointment ? [say(`profiles.seg.b.${s.appointment}` as StaffKey)] : []),
    ].join(' · ')

  const range = (q: ProfileQueue) => `${q.letter}${q.firstNumber} – ${q.letter}${q.lastNumber}`

  const queueBar = (barNode: NodeId, queueId: string) => {
    const q = queues.get(queueId)
    if (!q) return <span className="pf-qbar pf-qbar--gone">{say('profiles.problem.QUEUE_UNKNOWN')}</span>
    return (
      <button type="button" className="pf-qbar" {...node(barNode, { kind: 'queue', id: q.id })} {...pinned(pins.get(q.id))}>
        <span className="stub">{q.letter}</span>
        <span className="pf-qbar__body">
          <span className="pf-qbar__name">{nameIn(q.name, lang, q.code)}</span>
          <span className="pf-qbar__meta">
            <span className="pf-nb">({range(q)})</span>
            <span className="pf-nb" title={say('profiles.qs.target')}>{say('profiles.sl', { n: Math.round(q.serviceLevelSec / 60) })}</span>
            {q.type === 'appointment' && (
              <Chip size="sm" variant="secondary" className="pf-tag">
                {say('profiles.booked')}
              </Chip>
            )}
          </span>
        </span>
        {badge(pins.get(q.id))}
      </button>
    )
  }

  const rows = flow.lanes.length + 1
  const at = (column: number, row: number | string): CSSProperties => ({ gridColumn: column, gridRow: row })

  return (
    <div className="pf-frame">
      <div className="pf-flow" ref={setGrid} data-hovering={lit ? true : undefined}>
        <Connectors edges={flow.edges} anchors={layout.anchors} width={layout.w} height={layout.h} lit={lit?.edges ?? null} />

        <h3 className="pf-flow__head" style={at(1, 1)}>{say('profiles.col.services')}</h3>
        <h3 className="pf-flow__head" style={at(2, 1)}>{say('profiles.segmentation')}</h3>
        <h3 className="pf-flow__head" style={at(3, 1)}>{say('profiles.col.work')}</h3>

        {flow.lanes.map((lane, i) => {
          const g = groups.get(lane.groupId)!
          const shown = g.serviceIds.slice(0, SERVICE_LINES)
          const more = g.serviceIds.length - shown.length
          const defaultProblems = pins.get(g.id)?.filter((p) => p.path.endsWith('.defaultQueueId'))
          return (
            <Fragment key={lane.node}>
              <button
                type="button"
                className="pf-card pf-group"
                style={at(1, i + 2)}
                aria-label={g.name}
                {...node(lane.node, { kind: 'group', id: g.id })}
                {...pinned(pins.get(g.id))}
              >
                <span className="pf-card__name">{g.name}</span>
                <span className="pf-group__services">
                  {shown.map((s) => (
                    <span key={s} className="pf-card__line">{serviceName.get(s) ?? say('profiles.sheet.serviceGone')}</span>
                  ))}
                  {more > 0 && <span className="pf-card__line pf-card__more">+{more}</span>}
                </span>
                {badge(pins.get(g.id))}
              </button>

              <section className="pf-lane" style={at(2, i + 2)} aria-label={g.name}>
                <ol className="pf-lane__lines">
                  {lane.lines.map((l, n) => {
                    const s = l.segmentId ? g.segments.find((x) => x.id === l.segmentId) : undefined
                    const problems = s ? pins.get(s.id) : defaultProblems
                    const said = s ? conditions(s) : ''
                    return (
                      <li key={l.node} className="pf-lane__line">
                        <button
                          type="button"
                          className={s ? 'pf-rule-box' : 'pf-rule-box pf-rule-box--default'}
                          {...node(l.node, s ? { kind: 'segment', id: s.id, groupId: g.id } : { kind: 'group', id: g.id })}
                          {...pinned(problems)}
                        >
                          {s && <span className="pf-rule-box__n">{n + 1}</span>}
                          <span className="pf-rule-box__body">
                            <span className="pf-rule-box__name">{s ? s.name || say('profiles.segment') : say('profiles.default')}</span>
                            {said && <span className="pf-rule-box__said">{said}</span>}
                          </span>
                          {badge(problems)}
                        </button>
                        {queueBar(l.bar, l.queueId)}
                      </li>
                    )
                  })}
                </ol>
                {add('segment', 'profiles.add.rule', g.id, g.segments.length >= LIMITS.segmentsPerGroup)}
              </section>
            </Fragment>
          )
        })}

        <div className="pf-flow__foot" style={at(1, rows + 1)}>
          {doc.groups.length === 0 && <p className="pf-empty">{say('profiles.startGroups')}</p>}
          {add('group', 'profiles.add.group')}
        </div>

        <section
          className={flow.unrouted.length > 0 ? 'pf-lane pf-lane--loose' : 'pf-flow__foot'}
          style={at(2, rows + 1)}
          aria-label={say('profiles.unrouted')}
        >
          {flow.unrouted.length > 0 && (
            <>
              <h4 className="pf-lane__head">{say('profiles.unrouted')}</h4>
              <ul className="pf-lane__lines">
                {flow.unrouted.map((b) => (
                  <li key={b.node} className="pf-lane__line pf-lane__line--bar">
                    {queueBar(b.node, b.queueId)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {add('queue', 'profiles.add.queue')}
        </section>

        <section className="pf-workcol" style={at(3, `2 / ${rows + 2}`)} aria-label={say('profiles.col.work')}>
          {flow.work.map((id) => {
            const w = work.get(id.slice(2))!
            const calls = w.queueIds.flatMap((qid) => (queues.has(qid) ? [queues.get(qid)!] : []))
            return (
              <button key={id} type="button" className="pf-card pf-work" {...node(id, { kind: 'work', id: w.id })} {...pinned(pins.get(w.id))}>
                <span className="pf-card__name">{w.name}</span>
                {(calls.length > 0 || (w.pools?.length ?? 0) > 0) && (
                  <>
                    <span className="pf-work__label">{say('profiles.callFrom')}</span>
                    <span className="pf-work__calls">
                      {calls.map((q, k) => (
                        <span key={`${q.id}-${k}`} className="pf-qtag">
                          <span className="stub stub--sm">{q.letter}</span>
                          <span>{nameIn(q.name, lang, q.code)}</span>
                        </span>
                      ))}
                      {(w.pools ?? []).map((p) => (
                        <span key={p} className="pf-qtag pf-qtag--pool" title={say('profiles.wp.pools')}>
                          {say(`profiles.wp.pool.${p}`)}
                        </span>
                      ))}
                    </span>
                  </>
                )}
                {w.rules.length > 1 && (
                  <Chip size="sm" variant="secondary" className="pf-tag">
                    {say('profiles.nRules', { n: w.rules.length })}
                  </Chip>
                )}
                {badge(pins.get(w.id))}
              </button>
            )
          })}
          {add('work', 'profiles.add.work')}
        </section>
      </div>
    </div>
  )
}
